// web/src/routes/auth/Login.jsx
// Split layout: horse image on the left (public/horse.jpg), forest-green
// sign-in panel on the right with the lockup. Responsive — the image becomes
// a top banner on mobile.

import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, useLogin } from '../../lib/auth';
import Field, { UserIcon, LockIcon } from '../../components/ui/Field.jsx';
import lockupUrl from '../../assets/stallside-lockup-white.png';

export default function Login() {
  const { user } = useAuth();
  const login = useLogin();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/dashboard';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Once /me confirms a session (after a successful login, or if already
  // signed in), leave the login page. Waiting for `user` avoids bouncing off
  // RequireAuth before the session has propagated.
  useEffect(() => {
    if (user) navigate(from, { replace: true });
  }, [user, from, navigate]);

  function handleSubmit(e) {
    e.preventDefault();
    if (!email.trim() || !password || login.isPending) return;
    login.mutate({ email: email.trim(), password });
  }

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-[#1a2b1f]">
      {/* Image — top banner on mobile, left column on desktop */}
      <div className="relative h-48 sm:h-64 md:h-auto md:flex-1">
        <img src="/horse.jpg" alt="" className="absolute inset-0 w-full h-full object-cover" />
        {/* Blend the image into the panel color at the seam */}
        <div className="absolute inset-0 bg-gradient-to-b md:bg-gradient-to-r from-transparent to-[#1a2b1f]/90 md:to-[#1a2b1f]" />
        {/* Darken the bottom so the seam into the panel stays soft (desktop) */}
        <div className="hidden md:block absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/55 to-transparent" />
      </div>

      {/* Sign-in panel */}
      <div className="w-full md:w-[440px] lg:w-[480px] flex-1 md:flex-none flex flex-col text-[#f5f1e8]">
        <div className="flex-1 flex flex-col justify-center w-full max-w-sm mx-auto px-8 sm:px-12 py-10">
          <img src={lockupUrl} alt="stallSIDE" className="w-full h-auto mb-8" />

          <form onSubmit={handleSubmit} className="space-y-3">
            <Field
              icon={<UserIcon />}
              type="email"
              placeholder="Email"
              autoComplete="email"
              value={email}
              onChange={setEmail}
            />
            <Field
              icon={<LockIcon />}
              type="password"
              placeholder="Password"
              autoComplete="current-password"
              value={password}
              onChange={setPassword}
              reveal
            />

            {login.isError && (
              <p className="text-sm text-red-300">{friendlyError(login.error)}</p>
            )}

            <button
              type="submit"
              disabled={login.isPending}
              className="w-full py-3 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-semibold uppercase tracking-wide transition disabled:opacity-60"
            >
              {login.isPending ? 'Signing in…' : 'Sign In'}
            </button>
          </form>

          <p className="mt-6 text-sm text-center text-[#f5f1e8]/60">
            Need an account?{' '}
            <Link to="/signup" className="text-[#f5f1e8] underline hover:text-white">
              Sign up
            </Link>
          </p>
        </div>

        <footer className="px-8 sm:px-12 py-6 text-xs text-[#f5f1e8]/40 text-center">
          © {new Date().getFullYear()} stallSIDE. All rights reserved.
        </footer>
      </div>
    </div>
  );
}

function friendlyError(err) {
  const code = err?.body?.error || err?.message || '';
  switch (code) {
    case 'invalid_credentials':
    case 'unauthorized':
      return 'Incorrect email or password.';
    case 'missing_fields':
      return 'Enter your email and password.';
    default:
      return 'Couldn’t sign in. Try again.';
  }
}
